import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";
import { CHANGE_PASSWORD_SESSION_EXPIRED, mapChangePasswordError } from "../../lib/changePasswordErrors";
import { PASSWORD_REQUIREMENTS_HELPER, validateChangePasswordForm } from "../../lib/passwordPolicy";
import { supabase } from "../../supabaseClient";

const PasswordInput = ({ label, value, onChange, shown, toggle, autoComplete, disabled, helperText }) => (
  <div>
    <label className="mb-2 block text-sm text-gray-200">{label}</label>
    <div className="relative max-w-md">
      <input
        type={shown ? "text" : "password"}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={label}
        autoComplete={autoComplete}
        disabled={disabled}
        className="w-full rounded-md border border-white/10 bg-[#121212] px-3 py-2.5 pr-11 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-1 focus:ring-brand-maroon disabled:opacity-60"
      />
      <button
        type="button"
        onClick={toggle}
        disabled={disabled}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 transition hover:text-white disabled:opacity-60"
        aria-label={shown ? `Hide ${label}` : `Show ${label}`}
      >
        {shown ? <EyeOff size={18} /> : <Eye size={18} />}
      </button>
    </div>
    {helperText ? <p className="mt-1 text-xs text-gray-400">{helperText}</p> : null}
  </div>
);

const SecurityPanel = () => {
  const [loading, setLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const clearForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setMessage("");
    setError("");
  };

  const handleCancel = () => {
    if (loading) return;
    clearForm();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    setMessage("");

    const validationError = validateChangePasswordForm({
      currentPassword,
      newPassword,
      confirmPassword,
    });

    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    setLoading(true);

    try {
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError) throw userError;

      const email = String(user?.email || "").trim();
      if (!email) {
        setError(CHANGE_PASSWORD_SESSION_EXPIRED);
        return;
      }

      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (verifyError) throw verifyError;

      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) throw updateError;

      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setShowCurrentPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setMessage("Password changed successfully.");
    } catch (updateErr) {
      setError(mapChangePasswordError(updateErr));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-w-0 text-left">
      <h2 className="text-xs font-semibold tracking-widest text-gray-400 uppercase">Change password</h2>
      <form onSubmit={handleSubmit} className="mt-4" noValidate>
        <div className="space-y-4">
          <PasswordInput
            label="Current Password"
            value={currentPassword}
            onChange={setCurrentPassword}
            shown={showCurrentPassword}
            toggle={() => setShowCurrentPassword((prev) => !prev)}
            autoComplete="current-password"
            disabled={loading}
          />
          <PasswordInput
            label="New Password"
            value={newPassword}
            onChange={setNewPassword}
            shown={showNewPassword}
            toggle={() => setShowNewPassword((prev) => !prev)}
            autoComplete="new-password"
            disabled={loading}
            helperText={PASSWORD_REQUIREMENTS_HELPER}
          />
          <PasswordInput
            label="Confirm New Password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            shown={showConfirmPassword}
            toggle={() => setShowConfirmPassword((prev) => !prev)}
            autoComplete="new-password"
            disabled={loading}
          />
        </div>

        {message ? <p className="mt-4 text-sm text-emerald-400">{message}</p> : null}
        {error ? <p className="mt-4 text-sm text-red-400">{error}</p> : null}

        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row">
          <button
            type="button"
            onClick={handleCancel}
            disabled={loading}
            className="inline-flex min-h-10 w-full items-center justify-center rounded border border-gray-500 px-6 py-2.5 text-sm font-semibold text-gray-300 transition hover:bg-white/5 disabled:opacity-60 sm:w-auto"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex min-h-10 w-full items-center justify-center rounded bg-brand-maroon px-6 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60 sm:w-auto"
          >
            {loading ? "Changing Password..." : "Change Password"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SecurityPanel;
